import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { accommodationData } from '../mockData';
import { Hotel, Star, MapPin, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

export const Accommodation = () => {
  return (
    <div className="min-h-screen">
      <Navbar />
      
      {/* Header Section */}
      <section className="pt-32 pb-16 romantic-gradient">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center fade-in">
          <Hotel className="w-12 h-12 text-pink-400 mx-auto mb-6" />
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
            {accommodationData.title}
          </h1>
          <p className="font-sans text-lg md:text-xl text-muted-foreground">
            {accommodationData.description}
          </p>
        </div>
      </section>

      {/* Accommodation Options */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {accommodationData.options.map((option, index) => (
              <Card 
                key={option.id}
                className="overflow-hidden hover-lift border-pink-100 fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Image */}
                <div className="relative h-56 overflow-hidden">
                  <img 
                    src={option.image}
                    alt={option.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-white text-pink-600 font-semibold shadow-md">
                      {option.priceRange}
                    </Badge>
                  </div>
                  {option.type === 'Venue Hotel' && (
                    <div className="absolute top-4 left-4">
                      <Badge className="bg-pink-500 text-white font-semibold shadow-md">
                        <Star className="w-3 h-3 mr-1 fill-white" />
                        Recommended
                      </Badge>
                    </div>
                  )}
                </div>

                <CardHeader>
                  <div className="mb-2">
                    <span className="text-xs font-sans font-semibold text-pink-500 uppercase tracking-wider">
                      {option.type}
                    </span>
                  </div>
                  <CardTitle className="font-serif text-2xl mb-2">
                    {option.name}
                  </CardTitle>
                  <CardDescription className="text-base">
                    {option.description}
                  </CardDescription>
                </CardHeader>

                <CardContent>
                  {/* Amenities */}
                  <div className="mb-4">
                    <div className="flex flex-wrap gap-2">
                      {option.amenities.map((amenity) => (
                        <Badge 
                          key={amenity}
                          variant="outline"
                          className="border-pink-200 text-pink-700"
                        >
                          {amenity}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Distance */}
                  {option.distance && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                      <MapPin className="w-4 h-4 text-pink-500" />
                      <span className="font-sans">{option.distance} from venue</span>
                    </div>
                  )}

                  {/* Website Link */}
                  {option.website && (
                    <a
                      href={option.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-pink-600 hover:text-pink-700 font-sans font-medium transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Visit Website
                    </a>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Travel Tips */}
      <section className="py-20 bg-gradient-to-r from-green-50 via-pink-50 to-orange-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground text-center mb-10">
            Travel Tips
          </h2>
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <ul className="space-y-4">
              {accommodationData.travelTips.map((tip, index) => (
                <li 
                  key={tip}
                  className="flex items-start gap-4 fade-in"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-100 to-orange-100 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-pink-600 font-semibold text-sm">
                      {index + 1}
                    </span>
                  </div>
                  <p className="font-sans text-base text-muted-foreground flex-1">
                    {tip}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
